import FuzzySearch from 'fuzzy-search'
import { FilterByPipe } from 'ngx-pipes'
import { LocalStorageService } from 'ngx-store'
import { ChangeDetectionStrategy, Component, Input, ContentChild, Output, EventEmitter, Directive, TemplateRef, ElementRef, HostBinding, ChangeDetectorRef } from '@angular/core'
import { NaturalOrderByPipe, Selection } from 'common/util/module'
import { BaseComponent } from 'common/helpers'

export interface Sort<T extends string|object> {
  name: string
  qaName?: string
  keyFn: (item: T) => any[]
  reverse?: boolean
}

@Directive({ selector: '[entityListItem]' })
export class EntityListItemDirective {
  constructor (public templateRef: TemplateRef<unknown>) { }
}

@Directive({ selector: '[entityListEmpty]' })
export class EntityListEmptyDirective {
  constructor (public templateRef: TemplateRef<unknown>) { }
}

@Directive({ selector: '[entityListHeader]' })
export class EntityListHeaderDirective {
  constructor (public templateRef: TemplateRef<unknown>) { }
}

@Directive({ selector: '[entityListFooter]' })
export class EntityListFooterDirective {
  constructor (public templateRef: TemplateRef<unknown>) { }
}

@Directive({ selector: '[entityListControls]' })
export class EntityListControlsDirective {
  constructor (public templateRef: TemplateRef<unknown>) { }
}

@Component({
  selector: 'entity-list',
  templateUrl: './entityList.component.pug',
  styleUrls: ['./entityList.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [LocalStorageService],
})
export class EntityListComponent<T extends string|object> extends BaseComponent {
  @ContentChild(EntityListItemDirective) itemChild: EntityListItemDirective
  @ContentChild(EntityListEmptyDirective) emptyStateChild: EntityListEmptyDirective|undefined
  @ContentChild(EntityListHeaderDirective) headerChild: EntityListHeaderDirective|undefined
  @ContentChild(EntityListFooterDirective) footerChild: EntityListFooterDirective|undefined
  @ContentChild(EntityListControlsDirective) controlsChild: EntityListControlsDirective|undefined

  @Input() entities: T[]
  sortedEntities: T[]
  filteredEntities: T[]
  paginatedEntities: T[]

  @Input() selection?: Selection<T>
  @Input() sorts: Sort<T>[] = []

  @Input() sortEntityPaths: string[] = []
  @Input() trackByKey = ''
  @Input() className = 'list-group'
  @Input() showFilter = true
  @Input() filter = ''
  @Output() filterChange = new EventEmitter<string>()
  @Input() filterMode: 'fuzzy'|'exact' = 'fuzzy'
  @Input() filterKeys: string[] = []
  @Input() pageSize = 0
  @Input() page = 1
  @Output() pageChange = new EventEmitter<number>()
  @Input() autofocus = true

  private _sort: Sort<T>|null

  constructor (
    private element: ElementRef<HTMLElement>,
    private localStorage: LocalStorageService,
    private cdr: ChangeDetectorRef,
  ) {
    super()
    this.filterMode = this.localStorage.get(this.filterModePersistenceKey) ?? 'fuzzy'
  }

  ngOnInit () {
    if (this.sortEntityPaths.length && this.sorts.length) {
      throw new Error('sortEntityPaths and sorts are mutually exclusive')
    }

    this.className ??= 'list-group'
    if (this.sorts.length && !this.sort) {
      this.sort = this.sorts.find(s => s.name === this.localStorage.get(this.sortPersistenceKey)) ?? this.sorts[0]
    }

    this.resort()

    if (this.selection) {
      this.subs.add(this.selection.change$.subscribe(() => this.cdr.markForCheck()))
    }
  }

  ngOnChanges () {
    this.resort()
  }

  @Input('sort') set sort (sort: Sort<T>|null) {
    this._sort = sort
    this.resort()
    if (this.sorts.length && this.sort?.name === this.sorts[0].name) {
      this.localStorage.remove(this.sortPersistenceKey)
    } else {
      this.localStorage.set(this.sortPersistenceKey, this.sort?.name)
    }
  }

  get sort (): Sort<T>|null {
    return this._sort
  }

  resort () {
    const predicates: any[] = [...this.sortEntityPaths]
    if (this.sort) {
      predicates.unshift(this.sort.keyFn)
    }
    this.sortedEntities = new NaturalOrderByPipe().transform(
      this.entities,
      predicates,
    )
    if (this.sort?.reverse) {
      this.sortedEntities.reverse()
    }
    this.refilter()
  }

  refilter () {
    this.filteredEntities = this.sortedEntities
    if (this.filter) {
      if (this.filterMode === 'fuzzy') {
        this.filteredEntities = new FuzzySearch(
          this.sortedEntities,
          this.filterKeys,
          { sort: true },
        ).search(this.filter)
      } else {
        this.filteredEntities = new FilterByPipe().transform(
          this.filteredEntities,
          this.filterKeys,
          this.filter,
        )
      }
    }

    this.paginatedEntities = this.filteredEntities
    if (this.pageSize) {
      this.paginatedEntities = this.filteredEntities.slice((this.page - 1) * this.pageSize, this.page * this.pageSize)
    }
  }

  setFilterMode (mode: 'fuzzy'|'exact') {
    this.filterMode = mode
    this.localStorage.set(this.filterModePersistenceKey, this.filterMode)
    this.refilter()
  }

  trackBy = (entity: T) => {
    if (!this.trackByKey) {
      return null
    }
    return entity[this.trackByKey]
  }

  getAnimationDelay (index) {
    return Math.min(index / 20, 1)
  }

  @HostBinding('class.has-checkboxes') get hasCheckboxes () {
    return !!this.selection
  }

  @HostBinding('class.has-selection') get hasSelection () {
    return !this.selection?.isEmpty()
  }

  private get persistenceKeyPrefix () {
    return `entity-list-${location.pathname}-${this.element.nativeElement.getAttribute('entities')}`
  }

  private get sortPersistenceKey () {
    return `${this.persistenceKeyPrefix}-filter`
  }

  private get filterPersistenceKey () {
    return `${this.persistenceKeyPrefix}-sort`
  }

  private get filterModePersistenceKey () {
    return `${this.persistenceKeyPrefix}-filter-mode`
  }
}
